import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Menu, MenuItem, Typography, styled } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { ReactComponent as DashboardIcon } from "../../../assets/Dashboard.svg";
import { ReactComponent as UserManagementIcon } from "../../../assets/User Management.svg";
import { ReactComponent as ProviderOutreachIcon } from "../../../assets/Provider Outreach.svg";
import { ReactComponent as PharmacyOutreachIcon } from "../../../assets/Pharmacy Outreach.svg";
import { ReactComponent as ManualTasksIcon } from "../../../assets/Manual Tasks.svg";
import { ReactComponent as ReportingIcon } from "../../../assets/Reporting.svg";
import { ReactComponent as memberIcon } from "../../../assets/Members.svg";
import { ReactComponent as CagIcon } from "../../../assets/CagIcon.svg";
import { ReactComponent as CgAdminIcon } from "../../../assets/CgAdmin.svg";
import { usePermissions } from "../../../redux/permissions";

const StyledMenuIcon = styled("div", {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "18px",
  height: "18px",

  "&.members-icon": {
    width: "20px",
    height: "20px",
  },
  "& svg": {
    width: "100%",
    height: "100%",
    "& path, & rect, & circle, & polygon, & line, & polyline": {
      fill: "none",
      stroke: "#FFFFFF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      transition: "all 0.2s ease-in-out",
    },
  },

  "&.members-icon svg path, &.members-icon svg rect, &.members-icon svg circle, &.members-icon svg polygon, &.members-icon svg line, &.members-icon svg polyline":
    {
      strokeWidth: isActive ? "1.2" : "1",
    },

  "&:not(.members-icon) svg path, &:not(.members-icon) svg rect, &:not(.members-icon) svg circle, &:not(.members-icon) svg polygon, &:not(.members-icon) svg line, &:not(.members-icon) svg polyline":
    {
      strokeWidth: isActive ? "1.6" : "1.3",
    },
}));

const NavigationMenuIcon = ({ src: IconComponent, isActive, text }) => (
  <StyledMenuIcon
    isActive={isActive}
    className={
      text === "Members" || text === "Member Outreach" ? "members-icon" : ""
    }
  >
    <IconComponent />
  </StyledMenuIcon>
);

const menuItems = [
  {
    text: "Dashboard",
    iconSrc: DashboardIcon,
    path: "/dashboard",
    isClickable: true,
  },
  {
    text: "Member Outreach",
    iconSrc: memberIcon,
    path: "/members/member-outreach",
    isClickable: true,
  },
  {
    text: "Provider Outreach",
    iconSrc: ProviderOutreachIcon,
    path: "/provider-outreach",
    isClickable: true,
  },
  {
    text: "Pharmacy Outreach",
    iconSrc: PharmacyOutreachIcon,
    path: "/pharmacy-outreach",
    isClickable: true,
  },
  {
    text: "Manual Tasks",
    iconSrc: ManualTasksIcon,
    path: "/manual-tasks",
    isClickable: true,
  },
  {
    text: "Reporting",
    iconSrc: ReportingIcon,
    subItems: [
      { text: "Member Reporting", path: "/reporting/member-reporting" },
      { text: "Supervisor Reporting", path: "/reporting/supervisor-reporting" },
      { text: "Invoice Reporting", path: "/reporting/invoice-reporting" },
    ],
    isClickable: true,
  },
  {
    text: "Role Management",
    iconSrc: UserManagementIcon,
    path: "/role-management",
    isClickable: true,
  },
  {
    text: "CAG Configuration",
    customIcon: CagIcon,
    path: "/cag-configuration",
    isClickable: true,
  },
  {
    text: "CG+ Assessment Templates",
    customIcon: CgAdminIcon,
    path: "/cg-config",
    isClickable: true,
  },
];

export default function TopNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState("Member Outreach");
  const [activeSubItem, setActiveSubItem] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuFor, setOpenMenuFor] = useState(null);

  const moduleNames = [
    "Member Outreach",
    "Provider Outreach",
    "Pharmacy Outreach",
    "Manual Tasks",
    "Member Reporting",
    "Supervisor Reporting",
    "Invoice Reporting",
    "Role Management",
    "Dashboard",
    "CAG Configuration",
    "Assessment Configuration",
    "CG+ Assessment Templates",
  ];

  const permissionsMap = {};
  for (const name of moduleNames) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    permissionsMap[name] = usePermissions(name);
  }

  const filteredMenuItems = menuItems
    .map((item) => {
      if (!item.subItems) {
        return permissionsMap[item.text]?.canRead ? item : null;
      }
      const subItems = item.subItems.filter(
        (sub) => permissionsMap[sub.text]?.canRead,
      );
      return subItems.length > 0 ? { ...item, subItems } : null;
    })
    .filter((item) => item !== null);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/providerProfile/")) {
      setActiveItem("Provider Outreach");
      setActiveSubItem("");
      return;
    }
    if (path.includes("/pharmacyProfile/")) {
      setActiveItem("Pharmacy Outreach");
      setActiveSubItem("");
      return;
    }
    if (path.includes("/manual-tasks")) {
      setActiveItem("Manual Tasks");
      setActiveSubItem("");
      return;
    }

    for (const item of menuItems) {
      if (item.subItems) {
        const match = item.subItems.find(
          (sub) => sub.path === path || path.startsWith(sub.path),
        );
        if (match) {
          setActiveItem(item.text);
          setActiveSubItem(match.text);
          return;
        }
      } else if (
        item.path &&
        (path === item.path || path.startsWith(item.path))
      ) {
        setActiveItem(item.text);
        setActiveSubItem("");
        return;
      }
    }
  }, [location.pathname]);

  const handleMenuClick = (event, item) => {
    if (item.subItems && item.subItems.length > 0) {
      setAnchorEl(event.currentTarget);
      setOpenMenuFor(item.text);
    } else if (item.path) {
      setActiveItem(item.text);
      setActiveSubItem("");
      navigate(item.path);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuFor(null);
  };

  const handleSubItemClick = (subItem, parentText) => {
    setActiveItem(parentText);
    setActiveSubItem(subItem.text);
    handleMenuClose();
    if (subItem.path) {
      navigate(subItem.path);
    }
  };

  return (
    <Box
      component="nav"
      sx={{
        display: "flex",
        alignItems: "stretch",
        height: "100%",
        gap: { xs: 0.1, sm: 0.25, md: 0.5 },
        ml: { xs: 0.25, sm: 0.5, md: 1.5 },
        mr: { xs: 0.25, sm: 0.5, md: 1.5 },
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {filteredMenuItems.map((item) => {
        const isActive = activeItem === item.text;
        const isMenuOpen = openMenuFor === item.text;

        let menuIcon = null;
        if (item.iconSrc) {
          menuIcon = (
            <NavigationMenuIcon
              src={item.iconSrc}
              isActive={isActive}
              text={item.text}
            />
          );
        } else if (item.customIcon) {
          menuIcon = <item.customIcon style={{ width: 16, height: 16 }} />;
        }

        return (
          <React.Fragment key={item.text}>
            <Button
              onClick={(e) => handleMenuClick(e, item)}
              disableRipple
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: "3px", sm: "5px", md: "7px" },
                px: { xs: 0.75, sm: 1, md: 1.5 },
                py: 0,
                height: "100%",
                borderRadius: 0,
                backgroundColor: isActive
                  ? "rgba(255, 255, 255, 0.14)"
                  : "transparent",
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: { xs: "11px", sm: "12px", md: "13px" },
                fontWeight: isActive ? 700 : 500,
                whiteSpace: "nowrap",
                transition: "all 0.15s ease-in-out",
                borderBottom: isActive
                  ? "3px solid #FFFFFF"
                  : "3px solid transparent",
                boxSizing: "border-box",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: isActive
                    ? "rgba(255, 255, 255, 0.18)"
                    : "rgba(255, 255, 255, 0.08)",
                  color: "#FFFFFF",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: 14, sm: 16, md: 18 },
                  height: { xs: 14, sm: 16, md: 18 },
                  color: "#FFFFFF",
                  flexShrink: 0,
                }}
              >
                {menuIcon}
              </Box>

              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "11px", sm: "12px", md: "13px" },
                  fontWeight: isActive ? 700 : 500,
                  color: "#FFFFFF",
                  lineHeight: 1.2,
                }}
              >
                {item.text}
              </Typography>

              {item.subItems && (
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "#FFFFFF",
                  }}
                >
                  {isMenuOpen ? (
                    <ExpandLessIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                  )}
                </Box>
              )}
            </Button>

            {item.subItems && (
              <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    borderRadius: "4px",
                    minWidth: 180,
                  },
                }}
              >
                {item.subItems.map((subItem) => {
                  const isSubActive =
                    activeItem === item.text && activeSubItem === subItem.text;
                  return (
                    <MenuItem
                      key={subItem.text}
                      onClick={() => handleSubItemClick(subItem, item.text)}
                      sx={{
                        fontSize: "13px",
                        fontWeight: isSubActive ? 700 : 500,
                        color: "#403C6F",
                        backgroundColor: isSubActive
                          ? "#F2F5FF"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "#F2F5FF",
                        },
                      }}
                    >
                      {subItem.text}
                    </MenuItem>
                  );
                })}
              </Menu>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
